/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { ClubBenefitHeaderPill } from './ClubBenefitHeaderPill'
import { ButtonPrimary, ShareButton } from '@/components/button'
import { ChevronRight, PaddleIconFilledRounded } from '@/components/icons'
import { ConfirmModal } from '@/components/modal'
import { EventDetailHeaderLoading } from '@/components/skeleton'
import { CLUB_MODIFY, MarketingAssetsLink } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useApproveMembers, useJoinClub, useLeaveClub } from '@/services'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { getClubDetail, getClubRoles } from '../clubSlice'
import { clubSelector } from '../selectors'
import { ClubRestrictionBanners } from './ClubRestrictionBanners'

type Props = {
  onNavigateEditClub: () => void
  isAccountSetup: boolean
  onViewInfo: () => void
}

export const DetailHeader = ({ onNavigateEditClub, isAccountSetup, onViewInfo }: Props) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const { isLoading, club, permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const shareUrl = window.location.href
  const shareText = `Hi, I'm inviting you to become a member of the ${club?.clubName}’s club on DUPR \nPlease click the below Link to join the club.`
  const organizerDirectorPermission = permissions?.includes('ORGANIZER') || permissions?.includes('DIRECTOR')

  const { mutate: leaveClub, data: resLeaveClub } = useLeaveClub()
  const { mutate: joinClub, data: resJoinClub } = useJoinClub()
  const { mutate: approveMembers, data: resApproveMembers } = useApproveMembers()

  const [userRole, setUserRole] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [joinClubFail] = useState('')
  const [leaveClubFail] = useState('')

  const accountSetupErrors = club?.accountStatus?.errors

  const closeConfirmModal = () => setIsOpen(false)

  const onLeaveClub = () => {
    if (club) {
      leaveClub(club.clubId)
    }
  }

  useEffect(() => {
    if (resJoinClub?.status === 'FAILURE') {
      toast.error(resJoinClub?.message || t('club.joinClubFail'))
    }
  }, [resJoinClub])

  const onJoinClub = () => {
    if (club) {
      joinClub(club.clubId)
    }
  }

  const onApproveMembers = () => {
    if (club) {
      approveMembers(
        { clubId: Number(club.clubId), ids: [club.clubId] },
        {
          onSuccess: () => {
            closeConfirmModal()
            toast.success(t('club.acceptInvitation'))
          },
          onError: (error: any) => {
            closeConfirmModal()
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        }
      )
    }
  }

  const onDeclineInvite = () => {
    if (club) {
      leaveClub(club.clubId, {
        onSuccess: () => {
          closeConfirmModal()
          toast.success(t('club.declineInvitation'))
        },
        onError: (error: any) => {
          closeConfirmModal()
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        },
      })
    }
  }

  useEffect(() => {
    if (permissions) setUserRole(permissions[permissions.length - 1])
  }, [permissions])

  useEffect(() => {
    if (
      (resJoinClub?.status === 'SUCCESS' ||
        resLeaveClub?.status === 'SUCCESS' ||
        resApproveMembers?.status === 'SUCCESS') &&
      club &&
      user
    ) {
      dispatch(getClubRoles({ clubId: club.clubId, userId: user?.id }))
      dispatch(getClubDetail(club?.clubId.toString()))
    }
  }, [resJoinClub, resLeaveClub, resApproveMembers])

  return isLoading ? (
    <EventDetailHeaderLoading />
  ) : (
    <>
      <div className="flex">
        {club?.mediaUrl ? (
          <img src={club?.mediaUrl} className="w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-white" alt="" />
        ) : (
          <PaddleIconFilledRounded className="w-10 h-10 sm:w-16 sm:h-16 text-primary-500" />
        )}
        <div className="w-full flex flex-col gap-y-1 ml-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-y-1">
              <p className="text-white text-xl md:text-[32px] md:leading-10 font-bold">{club?.clubName}</p>
              <p className="text-white text-opacity-80 text-sm">{club?.shortAddress}</p>
              {/* With the address, because it is the same kind of fact about the club. */}
              <ClubBenefitHeaderPill clubId={club?.clubId ?? null} clubName={club?.clubName ?? ''} />
            </div>
            <button
              className="w-8 h-8 rounded-full bg-white flex md:hidden items-center justify-center bg-opacity-20"
              onClick={onViewInfo}
            >
              <ChevronRight className="text-white" />
            </button>
          </div>
          {userRole === 'PENDING_PLAYER' ? (
            <p className="font-semibold text-xs text-boldOrange">
              {user?.id === club?.requestedBy ? t('club.requested') : t('club.invited')}{' '}
              {`${moment(club?.created).fromNow()}`}
            </p>
          ) : null}
          <div className="flex py-1 md:py-2 gap-x-2">
            {has(permissions, CLUB_MODIFY) ? (
              <></>
            ) : (
              <>
                {isEmpty(permissions) ? (
                  <ButtonPrimary title={t('club.joinClub')} onClick={onJoinClub} />
                ) : userRole === 'PLAYER' ? (
                  <ButtonPrimary title={t('club.leaveClub')} onClick={onLeaveClub} />
                ) : userRole === 'PENDING_PLAYER' ? (
                  user?.id === club?.requestedBy ? (
                    <ButtonPrimary title={t('club.requestPending')} onClick={onLeaveClub} />
                  ) : (
                    <ButtonPrimary title={t('club.invitation')} onClick={() => setIsOpen(true)} />
                  )
                ) : null}
              </>
            )}
            {organizerDirectorPermission && (
              <a
                className="rounded-full py-2 px-4 bg-primary-500 hover:bg-opacity-70 text-white font-semibold text-sm"
                type="download"
                target="_blank"
                href={MarketingAssetsLink}
              >
                {t('common.marketingAssets')}
              </a>
            )}
            <ShareButton
              shareUrl={shareUrl}
              shareText={shareText}
              pageName={TRACKING_TYPES.CLUB_HOME}
              shareContent={TRACKING_TYPES.CLUB_SHARE}
            />
            {has(permissions, CLUB_MODIFY) && !isEmpty(club?.clubName) ? (
              <button
                className="py-2 px-4 text-white text-sm font-semibold hover:text-opacity-70 hidden sm:flex"
                onClick={onNavigateEditClub}
              >
                {t('club.editClub')}
              </button>
            ) : null}
          </div>

          {has(permissions, CLUB_MODIFY) && !isAccountSetup
            ? !isEmpty(accountSetupErrors) && (
                <p className="font-semibold text-xs text-boldOrange">{t('club.fixClubPayments')}</p>
              )
            : null}
          {joinClubFail ? <p className="font-semibold text-xs text-boldOrange">{joinClubFail}</p> : null}
          {leaveClubFail ? <p className="font-semibold text-xs text-boldOrange">{leaveClubFail}</p> : null}
        </div>
      </div>
      {!isLoading && (
        <ClubRestrictionBanners
          clubId={club?.clubId}
          urns={club?.restrictions ?? []}
          isStaff={!!organizerDirectorPermission}
        />
      )}
      <ConfirmModal
        open={isOpen}
        onClose={closeConfirmModal}
        title={t('club.invitation')}
        desc={t('club.invitationDesc')}
        submitTitle={t('common.accept')}
        cancelTitle={t('common.decline')}
        onSubmit={onApproveMembers}
        onCloseButton={onDeclineInvite}
      />
    </>
  )
}
